import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { AlertCircle, Check } from "@untitledui/icons";

export interface SearchParams {
	domain: string;
	isPublic: string;
	replayUserId?: string;
	replayEmail?: string;
	replayPhone?: string;
	replayInvitationType?: string;
}

export const Route = createFileRoute("/system/classify-domain")({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		domain: (search.domain as string) ?? "",
		isPublic: (search.isPublic as string) ?? "",
		replayUserId: search.replayUserId as string | undefined,
		replayEmail: search.replayEmail as string | undefined,
		replayPhone: search.replayPhone as string | undefined,
		replayInvitationType: search.replayInvitationType as string | undefined,
	}),
	component: ClassifyDomainPage,
});

function ClassifyDomainPage() {
	const { domain, isPublic, replayUserId, replayEmail, replayPhone, replayInvitationType } = Route.useSearch();
	const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
	const [message, setMessage] = useState("");

	const isPublicBool = isPublic === "true";
	const label = isPublicBool ? "public" : "private";
	const calledRef = useRef(false);

	useEffect(() => {
		if (calledRef.current) return;
		calledRef.current = true;

		if (!domain) {
			setStatus("error");
			setMessage("Missing domain parameter.");
			return;
		}

		axios
			.post("/api/system/email-domain-classifications", {
				domain,
				isPublic: isPublicBool,
				replayUserId: replayUserId || undefined,
				replayEmail: replayEmail || undefined,
				replayPhone: replayPhone || undefined,
				replayInvitationType: replayInvitationType || undefined,
			})
			.then((res) => {
				setStatus("success");
				setMessage(res.data.message);
			})
			.catch((err) => {
				setStatus("error");
				setMessage(err.response?.data?.message ?? "Failed to classify domain.");
			});
	}, [domain, isPublicBool]);

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50 p-8">
			<div className="w-full max-w-md rounded-xl bg-white p-8 shadow-sm">
				{status === "loading" && (
					<div className="text-center">
						<p className="text-gray-500">Classifying <strong>{domain}</strong> as {label}...</p>
					</div>
				)}
				{status === "success" && (
					<div className="text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
							<Check className="h-6 w-6 text-green-600" />
						</div>
						<h2 className="mb-2 text-lg font-semibold text-gray-900">Domain Classified</h2>
						<p className="text-gray-500">{message}</p>
					</div>
				)}
				{status === "error" && (
					<div className="text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
							<AlertCircle className="h-6 w-6 text-red-600" />
						</div>
						<h2 className="mb-2 text-lg font-semibold text-gray-900">Classification Failed</h2>
						<p className="text-gray-500">{message}</p>
					</div>
				)}
			</div>
		</div>
	);
}